/* VØRIX Premium — core module (theme v4)
 * No dependencies. Exposes a small API on window.vorix for vorix-product.js.
 * Storage keys are unchanged from v3 so saved and recently viewed items carry over.
 */

const config = window.vorix || { routes: {}, settings: {}, strings: {} };
const root = document.documentElement;
const qs = (selector, scope = document) => scope.querySelector(selector);
const qsa = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));

const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
const motion = () => (prefersReduced.matches ? 'off' : root.dataset.motion || 'full');
const isDesktop = window.matchMedia('(min-width: 1024px)');
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

if (prefersReduced.matches) root.dataset.motion = 'off';

const storage = {
  get(key, fallback) {
    try {
      const value = JSON.parse(localStorage.getItem(key) || 'null');
      if (value === null || (Array.isArray(fallback) && !Array.isArray(value))) return fallback;
      return Array.isArray(value) ? value.filter((item) => item && typeof item === 'object' && item.handle) : value;
    } catch (_) {
      return fallback;
    }
  },
  set(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (_) {
      /* storage blocked (private mode); features degrade quietly */
    }
  },
  raw(key, value) {
    try {
      if (value === undefined) return localStorage.getItem(key);
      localStorage.setItem(key, value);
    } catch (_) {
      return null;
    }
    return null;
  },
};

const escapeHtml = (value = '') =>
  String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

/* ---------- Toast ---------- */
let toastTimer;
function toast(message) {
  const el = qs('[data-toast]');
  if (!el || !message) return;
  clearTimeout(toastTimer);
  el.textContent = '';
  el.classList.add('is-hiding');
  setTimeout(() => {
    el.textContent = message;
    requestAnimationFrame(() => el.classList.remove('is-hiding'));
  }, 60);
  toastTimer = setTimeout(() => {
    el.classList.add('is-hiding');
    setTimeout(() => (el.textContent = ''), 260);
  }, 2600);
}

/* ---------- Section rendering ---------- */
async function fetchSectionHTML(url) {
  const response = await fetch(url, { headers: { Accept: 'text/html' } });
  if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
  return response.text();
}
const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

/* ---------- Sheets (native <dialog>) ---------- */
const sheets = new Map();
let lastTrigger = null;

function openSheet(id, trigger) {
  const dialog = document.getElementById(id);
  if (!dialog) return;
  if (dialog.open) {
    if (dialog.dataset.closing) {
      clearTimeout(dialog._closeTimer);
      delete dialog.dataset.closing;
      dialog.classList.add('is-open');
      qsa(`[aria-controls="${id}"]`).forEach((t) => t.setAttribute('aria-expanded', 'true'));
    }
    return;
  }
  qsa('dialog[open][data-sheet]').forEach((other) => other !== dialog && closeSheet(other, { restoreFocus: false }));
  lastTrigger = trigger || document.activeElement;
  root.style.setProperty('--sbw', `${Math.max(0, window.innerWidth - root.clientWidth)}px`);
  dialog.showModal();
  root.classList.add('is-locked');
  root.classList.remove('header-hidden');
  qsa(`[aria-controls="${id}"]`).forEach((t) => t.setAttribute('aria-expanded', 'true'));
  requestAnimationFrame(() => requestAnimationFrame(() => dialog.classList.add('is-open')));
  dialog.dispatchEvent(new CustomEvent('sheet:open'));
}

function closeSheet(dialogOrId, { restoreFocus = true } = {}) {
  const dialog = typeof dialogOrId === 'string' ? document.getElementById(dialogOrId) : dialogOrId;
  if (!dialog || !dialog.open || dialog.dataset.closing) return;
  dialog.dataset.closing = '1';
  dialog.classList.remove('is-open');
  qsa(`[aria-controls="${dialog.id}"]`).forEach((t) => t.setAttribute('aria-expanded', 'false'));
  const done = () => {
    delete dialog.dataset.closing;
    dialog.close();
    if (!qs('dialog[open]')) root.classList.remove('is-locked');
    if (restoreFocus && lastTrigger && document.contains(lastTrigger) && !lastTrigger.closest('dialog:not([open])')) {
      lastTrigger.focus({ preventScroll: true });
    }
    dialog.dispatchEvent(new CustomEvent('sheet:close'));
  };
  if (motion() === 'off') return done();
  dialog._closeTimer = setTimeout(done, 400);
}

function initSheets() {
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open]');
    if (opener) {
      const id = opener.dataset.open;
      if (id === 'CartDrawer' && config.settings.cartType === 'page') return;
      if (!document.getElementById(id)) return;
      event.preventDefault();
      const fromSheet = opener.closest('dialog[open]');
      if (fromSheet && fromSheet.id !== id) {
        const pageTrigger = lastTrigger && !lastTrigger.closest('dialog') ? lastTrigger : qs(`[aria-controls="${fromSheet.id}"]`);
        closeSheet(fromSheet, { restoreFocus: false });
        setTimeout(() => openSheet(id, pageTrigger || opener), motion() === 'off' ? 0 : 200);
      } else {
        openSheet(id, opener);
      }
      return;
    }
    const closer = event.target.closest('[data-close]');
    if (closer) {
      const dialog = closer.closest('dialog[data-sheet]');
      if (dialog && !closer.dataset.open) closeSheet(dialog);
    }
  });

  qsa('dialog[data-sheet]').forEach((dialog) => {
    sheets.set(dialog.id, dialog);
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeSheet(dialog);
    });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeSheet(dialog);
    });
  });
}

/* ---------- Header ---------- */
function initHeader() {
  const header = qs('[data-header]');
  if (!header) return;
  const overlay = header.classList.contains('header--overlay');
  const hideOnScroll = header.dataset.hideOnScroll === 'true';
  let lastY = window.scrollY;
  let ticking = false;

  const update = () => {
    const y = window.scrollY;
    if (overlay) header.classList.toggle('is-solid', y > 8 || header.classList.contains('panel-open'));
    if (hideOnScroll && !root.classList.contains('is-locked')) {
      const down = y > lastY + 4;
      const up = y < lastY - 4;
      const hidden = root.classList.contains('header-hidden');
      if (down && y > 160 && !hidden) root.classList.add('header-hidden');
      else if ((up || y < 80) && hidden) root.classList.remove('header-hidden');
    }
    lastY = y;
    ticking = false;
  };
  window.addEventListener(
    'scroll',
    () => {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    },
    { passive: true },
  );
  update();
  header.addEventListener('focusin', () => root.classList.remove('header-hidden'));

  // Shop panels (clothing, accessories): hover intent on desktop, keyboard friendly. Opening one closes the other.
  const panels = [];
  qsa('[data-nav-panel]', header).forEach((item) => {
    const toggle = qs('[data-panel-toggle]', item);
    const panel = toggle && document.getElementById(toggle.getAttribute('aria-controls'));
    if (panel) panels.push(initShopPanel(header, item, toggle, panel, overlay, panels));
  });
}

function initShopPanel(header, item, toggle, panel, overlay, panels) {
  const link = qs('.nav__link', item);
  let timer;
  let openedBy = null;
  const setOpen = (open, by = 'hover') => {
    openedBy = open ? by : null;
    clearTimeout(timer);
    if (open) {
      panels.forEach((other) => other.panel !== panel && other.close());
      panel.hidden = false;
      requestAnimationFrame(() => panel.classList.add('is-open'));
    } else {
      panel.classList.remove('is-open');
      timer = setTimeout(() => (panel.hidden = true), 240);
    }
    item.classList.toggle('is-open', open);
    header.classList.toggle('panel-open', open || panels.some((other) => other.panel !== panel && other.isOpen()));
    if (overlay) header.classList.toggle('is-solid', open || window.scrollY > 8 || header.classList.contains('panel-open'));
    toggle.setAttribute('aria-expanded', String(open));
  };
  toggle?.addEventListener('click', () => {
    // A click right after hover-open pins the panel instead of closing it.
    const open = panel.hidden || openedBy === 'hover';
    setOpen(open, 'click');
    if (open) requestAnimationFrame(() => qs('a', panel)?.focus());
  });
  const scheduleClose = () => {
    clearTimeout(timer);
    timer = setTimeout(() => setOpen(false), 160);
  };
  item.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && isDesktop.matches && setOpen(true));
  item.addEventListener('pointerleave', (e) => e.pointerType === 'mouse' && openedBy !== 'click' && scheduleClose());
  panel.addEventListener('pointerenter', () => clearTimeout(timer));
  panel.addEventListener('pointerleave', () => openedBy !== 'click' && scheduleClose());
  link.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      qs('a', panel)?.focus();
    }
  });
  header.addEventListener('focusout', (event) => {
    if (!header.contains(event.relatedTarget) || (!panel.contains(event.relatedTarget) && !item.contains(event.relatedTarget))) {
      if (!panel.hidden) setOpen(false);
    }
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !panel.hidden) {
      setOpen(false);
      (toggle || link).focus();
    }
  });
  const isOpen = () => item.classList.contains('is-open');
  return { panel, isOpen, close: () => isOpen() && setOpen(false) };
}

/* ---------- Zero Hour: countdown with odometer digits, and unlocking in place ---------- */
const zeroHourTarget = () => new Date(config.settings.zeroHour || '').getTime();

/** Purchasing UI for both states is in the page; this flips which one shows. Pages may be served
 *  from Shopify's cache, so the switch happens here rather than relying on Liquid's render time. */
function unlockZeroHour() {
  if (!root.hasAttribute('data-zero-hour-locked')) return;
  root.removeAttribute('data-zero-hour-locked');
  config.settings.zeroHourLocked = false;
  document.dispatchEvent(new CustomEvent('vorix:zero-hour'));
}

function initCountdowns() {
  const target = zeroHourTarget();
  if (!Number.isFinite(target)) return;
  if (Date.now() >= target) {
    unlockZeroHour();
  }
  const animate = () => motion() !== 'off' && !document.hidden;
  const pad = (n) => String(n).padStart(2, '0');

  const setDigits = (valueEl, text) => {
    const odos = qsa('.odo', valueEl);
    if (odos.length !== text.length) {
      valueEl.innerHTML = text
        .split('')
        .map((c) => `<span class="odo"><span class="odo__digit">${c}</span></span>`)
        .join('');
      return;
    }
    text.split('').forEach((char, i) => {
      const odo = odos[i];
      qsa('.odo__digit--next', odo).forEach((n) => n.remove());
      const current = odo.querySelector('.odo__digit');
      if (!current || current.textContent === char) return;
      if (!animate()) {
        current.textContent = char;
        return;
      }
      const next = document.createElement('span');
      next.className = 'odo__digit odo__digit--next';
      next.textContent = char;
      odo.appendChild(next);
      const opts = { duration: 240, easing: 'cubic-bezier(.16,1,.3,1)' };
      current.animate([{ transform: 'translateY(0)' }, { transform: 'translateY(-100%)' }], opts);
      next
        .animate([{ transform: 'translateY(100%)' }, { transform: 'translateY(0)' }], opts)
        .finished.then(() => {
          current.textContent = char;
          next.remove();
        })
        .catch(() => {});
    });
  };

  const goLive = () => {
    qsa('[data-countdown]').forEach((clock) => {
      const wrap = document.createElement('p');
      wrap.className = 'countdown countdown--live';
      wrap.setAttribute('role', 'status');
      wrap.textContent = config.settings.zeroHourLive || 'Live now.';
      clock.replaceWith(wrap);
    });
    unlockZeroHour();
  };

  let spokenMinute = -1;
  const tick = () => {
    const clocks = qsa('[data-countdown]'); // re-queried: sections can be re-rendered
    const diff = Math.max(0, target - Date.now());
    if (diff === 0) {
      goLive();
      return false;
    }
    if (!clocks.length) return true;
    const total = Math.floor(diff / 1000);
    const values = {
      days: pad(Math.floor(total / 86400)),
      hours: pad(Math.floor((total % 86400) / 3600)),
      minutes: pad(Math.floor((total % 3600) / 60)),
      seconds: pad(total % 60),
    };
    const minute = Math.floor(total / 60);
    const speak = minute !== spokenMinute;
    spokenMinute = minute;
    clocks.forEach((clock) => {
      Object.entries(values).forEach(([unit, text]) => {
        const unitEl = qs(`[data-unit="${unit}"]`, clock);
        if (!unitEl) return;
        const valueEl = qs('[data-value]', unitEl);
        if (valueEl) setDigits(valueEl, text);
        if (speak) {
          const sr = qs('.countdown__label .visually-hidden', unitEl);
          if (sr) sr.textContent = sr.textContent.replace(/^\d+/, text);
        }
      });
    });
    return true;
  };
  if (Date.now() < target && tick()) {
    const timer = setInterval(() => tick() || clearInterval(timer), 1000);
  }
}

/* ---------- Hero "throttle": the one orchestrated load moment ---------- */
function initHero() {
  // Under the intro the hero would play unseen; it plays the moment the intro clears instead.
  if (root.classList.contains('vx-intro-on')) {
    document.addEventListener('vorix:intro-end', initHero, { once: true });
    return;
  }
  const title = qs('[data-throttle]');
  if (!title || motion() !== 'full' || !title.animate) return;
  // Rise and sharpen (transform, opacity and blur only: animating the font's width reflowed the headline every frame).
  const lines = qsa('.hero__line', title);
  lines.forEach((line, i) => {
    line.animate(
      [
        { transform: 'translateY(0.28em) scale(1.03)', opacity: 0, filter: 'blur(10px)' },
        { transform: 'translateY(0) scale(1)', opacity: 1, filter: 'blur(0px)' },
      ],
      { duration: 1100, delay: 80 + i * 110, easing: 'cubic-bezier(.16,1,.3,1)', fill: 'backwards' },
    );
  });
  // The hero image is the page's LCP element, so it stays visible from the first frame; it only settles in scale.
  const heroImg = qs('.hero__plate .plate__img');
  if (heroImg) {
    heroImg.animate([{ transform: 'scale(1.045)' }, { transform: 'scale(1)' }], {
      duration: 1400,
      easing: 'cubic-bezier(.16,1,.3,1)',
    });
  }
}

/* ---------- Image reveals ---------- */
function initReveals(scope = document) {
  const items = qsa('[data-reveal]:not(.is-revealed)', scope);
  if (!items.length) return;
  if (motion() !== 'full' || !config.settings.revealImages || !('IntersectionObserver' in window)) {
    items.forEach((el) => el.classList.add('is-revealed'));
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        io.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.01 },
  );
  items.forEach((el) => io.observe(el));
}

/* ---------- Tabs ---------- */
function initTabs(scope = document) {
  qsa('[data-tabs]', scope).forEach((wrap) => {
    const tabs = qsa('[data-tab]', wrap);
    const panels = qsa('[data-panel]', wrap);
    const select = (tab, focus = false) => {
      tabs.forEach((t, i) => {
        const on = t === tab;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        panels[i].hidden = !on;
        if (on) initReveals(panels[i]);
      });
      if (focus) tab.focus();
    };
    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => select(tab));
      tab.addEventListener('keydown', (event) => {
        const keys = { ArrowRight: 1, ArrowLeft: -1, Home: -Infinity, End: Infinity };
        if (!(event.key in keys)) return;
        event.preventDefault();
        const step = keys[event.key];
        const nextIndex = Number.isFinite(step) ? (index + step + tabs.length) % tabs.length : step > 0 ? tabs.length - 1 : 0;
        select(tabs[nextIndex], true);
      });
    });
  });
}

/* ---------- Front / Back view toggle ---------- */
function initViewToggle() {
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-view]');
    if (!btn || btn.tagName !== 'BUTTON') return;
    const section = btn.closest('section');
    if (!section) return;
    const view = btn.dataset.view;
    section.dataset.view = view;
    qsa('button[data-view]', section).forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
  });
}

/* ---------- Saved pieces ---------- */
const SAVED_KEY = 'vorix.saved.v1';
const RECENT_KEY = 'vorix.recent.v1';
const getSaved = () => storage.get(SAVED_KEY, []);
const setSaved = (items) => storage.set(SAVED_KEY, items.slice(0, 24));

const itemFrom = (el) => {
  const item = { handle: el.dataset.handle, title: el.dataset.title, url: el.dataset.url, image: el.dataset.image, price: el.dataset.price };
  if (el.dataset.type) item.type = el.dataset.type;
  if (el.dataset.sizes) item.sizes = el.dataset.sizes;
  if (el.dataset.colour) item.colour = el.dataset.colour;
  return item;
};

// A saved piece is a product in one colour. Items saved before colours existed have no colour
// and count as the product's default colour (Black), so they stay saved.
const savedMatch = (item, el) =>
  item.handle === el.dataset.handle && (item.colour || el.dataset.colourDefault || '') === (el.dataset.colour || '');

function syncSaved() {
  const items = getSaved();
  qsa('[data-save]').forEach((btn) => {
    const on = items.some((item) => savedMatch(item, btn));
    btn.setAttribute('aria-pressed', String(on));
    const label = qs('[data-save-label]', btn);
    if (label) label.textContent = on ? label.dataset.labelOn : label.dataset.labelOff;
  });
  qsa('[data-saved-count]').forEach((el) => {
    el.textContent = String(items.length);
    el.hidden = items.length === 0;
  });
  qsa('[data-saved-count-text]').forEach((el) => (el.textContent = items.length ? String(items.length) : ''));
  qsa('[data-saved-button]').forEach((el) =>
    el.setAttribute('aria-label', items.length ? `${config.strings.savedLabel || 'Saved'}, ${items.length}` : config.strings.savedLabel || 'Saved'),
  );
  const list = qs('[data-saved-list]');
  if (list) {
    list.innerHTML = items
      .map(
        (item) => `
      <li class="line">
        <a class="line__media" href="${escapeHtml(item.url)}" tabindex="-1" aria-hidden="true">
          <div class="plate" style="--plate-ratio: 4/5">${item.image ? `<img class="plate__img" src="${escapeHtml(item.image)}" alt="" loading="lazy" width="720" height="900">` : ''}</div>
        </a>
        <div class="line__info">
          <a class="line__title" href="${escapeHtml(item.url)}">${escapeHtml(item.title)}</a>
          <p class="line__meta">${item.colour ? `${escapeHtml(item.colour)} · ` : ''}${escapeHtml(item.price || '')}</p>
          <button class="line__remove" type="button" data-unsave="${escapeHtml(item.handle)}" data-unsave-colour="${escapeHtml(item.colour || '')}">Remove</button>
        </div>
      </li>`,
      )
      .join('');
    const empty = qs('[data-saved-empty]');
    if (empty) empty.hidden = items.length > 0;
  }
}

function initSaved() {
  // Refresh stored titles and prices from what the page currently shows. The image and link only
  // change for the same colour, so a saved Sky Blue keeps its Sky Blue photo.
  const buttons = qsa('[data-save]');
  if (buttons.length) {
    setSaved(
      getSaved().map((item) => {
        const same = buttons.find((b) => savedMatch(item, b));
        if (same) return { ...item, ...itemFrom(same), colour: item.colour || same.dataset.colour };
        const other = buttons.find((b) => b.dataset.handle === item.handle);
        return other ? { ...item, title: other.dataset.title, price: other.dataset.price } : item;
      }),
    );
  }

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-save]');
    if (btn) {
      event.preventDefault();
      const item = itemFrom(btn);
      let items = getSaved();
      const exists = items.some((i) => savedMatch(i, btn));
      items = exists ? items.filter((i) => !savedMatch(i, btn)) : [item, ...items];
      setSaved(items);
      syncSaved();
      toast(exists ? config.strings.removed : item.colour ? `${config.strings.saved} · ${item.colour}` : config.strings.saved);
      return;
    }
    const remove = event.target.closest('[data-unsave]');
    if (remove) {
      const colour = remove.dataset.unsaveColour || '';
      setSaved(getSaved().filter((i) => !(i.handle === remove.dataset.unsave && (i.colour || '') === colour)));
      syncSaved();
    }
  });
  syncSaved();
}

/* ---------- Recently viewed ---------- */
function initRecent() {
  const current = qs('[data-recent]');
  if (current) {
    const item = itemFrom(current);
    const items = storage.get(RECENT_KEY, []).filter((i) => i.handle !== item.handle);
    storage.set(RECENT_KEY, [item, ...items].slice(0, 8));
  }
  const section = qs('[data-recent-section]');
  const grid = qs('[data-recent-grid]');
  if (!section || !grid) return;
  const onPage = new Set(qsa('[data-card]').map((c) => c.dataset.handle));
  if (current) onPage.add(current.dataset.handle);
  const items = storage.get(RECENT_KEY, []).filter((i) => !onPage.has(i.handle)).slice(0, 4);
  if (items.length < 2) return;
  grid.innerHTML = items
    .map((item) => {
      const [name, number] = splitTitle(item.title);
      return `
      <li><article class="card">
        <a class="card__link" href="${escapeHtml(item.url)}">
          <div class="card__media"><div class="plate card__plate" style="--plate-ratio: var(--card-ratio)">${
            item.image ? `<img class="plate__img" src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy" width="720" height="900">` : ''
          }</div></div>
          <div class="card__meta">
            <h3 class="card__title">${escapeHtml(name)}</h3>
            <span class="price card__price">${escapeHtml(item.price || '')}</span>
            <p class="card__sub">${number ? `<span class="card__num">${escapeHtml(number)}</span>` : ''}${item.type ? `<span>${escapeHtml(item.type)}</span>` : ''}${item.sizes ? `<span class="card__sizes">${escapeHtml(item.sizes)}</span>` : ''}</p>
          </div>
        </a>
        <button class="card__save save-toggle" type="button" data-save data-handle="${escapeHtml(item.handle)}" data-title="${escapeHtml(item.title)}" data-url="${escapeHtml(item.url)}" data-image="${escapeHtml(item.image || '')}" data-price="${escapeHtml(item.price || '')}"${item.colour ? ` data-colour="${escapeHtml(item.colour)}"` : ''} aria-pressed="false" aria-label="Save ${escapeHtml(item.title)}">
          <svg class="icon icon--save" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6.75 3.75h10.5v16.5L12 16.6l-5.25 3.65V3.75Z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><path class="icon__fill" d="M6.75 3.75h10.5v16.5L12 16.6l-5.25 3.65V3.75Z" fill="currentColor"/></svg>
        </button>
      </article></li>`;
    })
    .join('');
  section.hidden = false;
  syncSaved();
}

function splitTitle(title = '') {
  const match = String(title).match(/^(.*?)\s*\/\s*(\d+)$/);
  return match ? [match[1], match[2]] : [title, ''];
}

/* ---------- Cart ---------- */
const CART_SECTION = 'cart-drawer';

function updateCartCount(count) {
  qsa('[data-cart-count]').forEach((el) => {
    el.textContent = String(count);
    el.hidden = count === 0;
  });
  qsa('[data-cart-link]').forEach((el) => {
    const label = el.getAttribute('aria-label') || '';
    el.setAttribute('aria-label', label.replace(/\d+/, String(count)));
  });
}

/** Read a Cart API response; never show raw parser or network errors to shoppers. */
async function readCartResponse(response) {
  let data = {};
  try {
    data = await response.json();
  } catch (_) {
    throw new Error(config.strings.cartError);
  }
  if (!response.ok || data.status) throw new Error(data.description || data.message || config.strings.cartError);
  return data;
}

/** Where focus was inside a cart line, so it can be put back after the HTML is replaced. */
function rememberCartFocus() {
  const el = document.activeElement;
  const line = el?.closest?.('[data-key]');
  if (!line) return null;
  const control = el.matches('[data-qty-change="1"]') ? '[data-qty-change="1"]' : el.matches('[data-qty-change="-1"]') ? '[data-qty-change="-1"]' : el.matches('[data-qty-input]') ? '[data-qty-input]' : '[data-remove]';
  return { key: line.dataset.key, control, scope: line.closest('[data-cart-root], [data-cart-page]') };
}

function restoreCartFocus(memo) {
  if (!memo) return;
  const scope = memo.scope?.isConnected ? memo.scope : document;
  const line = qs(`[data-key="${CSS.escape(memo.key)}"]`, scope);
  const target = line ? qs(memo.control, line) : qs('.sheet__title, .page-head h1', scope.closest?.('dialog, section') || document);
  if (target) {
    if (!target.hasAttribute('tabindex') && !/^(A|BUTTON|INPUT)$/.test(target.tagName)) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }
}

function renderCartDrawer(html) {
  if (!html) return;
  const doc = parseHTML(html);
  const next = qs('[data-cart-root]', doc);
  const current = qs('[data-cart-root]');
  if (!next || !current) return;
  current.replaceChildren(...next.childNodes);
  current.dataset.count = next.dataset.count;
  updateCartCount(Number(next.dataset.count || 0));
}

function renderCartPage(html) {
  const page = qs('[data-cart-page]');
  if (!page || !html) return;
  const next = qs('[data-cart-page]', parseHTML(html));
  if (!next) return;
  const note = qs('[name="note"]', page)?.value;
  page.replaceChildren(...next.childNodes);
  const noteField = qs('[name="note"]', page);
  if (noteField && note !== undefined) noteField.value = note;
  initReveals(page);
  syncSaved();
}

async function cartAdd(formData) {
  formData.append('sections', CART_SECTION);
  formData.append('sections_url', window.location.pathname);
  const response = await fetch(`${config.routes.cartAdd}.js`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: formData,
  });
  const data = await readCartResponse(response);
  if (config.settings.cartType === 'page') {
    window.location.href = config.routes.cart;
    return data;
  }
  renderCartDrawer(data.sections?.[CART_SECTION]);
  try {
    await config.beforeCartOpen?.(); // v5: the star flies into the bag first (vorix-plus.js)
  } catch (_) {
    /* never hold up the bag */
  }
  openSheet('CartDrawer');
  return data;
}

/** Change a line by its key (stable while other lines are removed), not by its position. */
async function cartChange(key, quantity) {
  const page = qs('[data-cart-page]');
  const sections = [CART_SECTION];
  if (page?.dataset.sectionId) sections.push(page.dataset.sectionId);
  const response = await fetch(`${config.routes.cartChange}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ id: key, quantity, sections: sections.join(','), sections_url: window.location.pathname }),
  });
  const data = await readCartResponse(response);
  const focus = rememberCartFocus();
  renderCartDrawer(data.sections?.[CART_SECTION]);
  if (page) renderCartPage(data.sections?.[page.dataset.sectionId]);
  restoreCartFocus(focus);
  return data;
}

function initCart() {
  let pending = Promise.resolve();
  const queue = (fn) => (pending = pending.then(fn, fn));
  const lineByKey = (key) => qsa(`[data-key="${CSS.escape(key)}"]`).find((el) => el.isConnected);

  const run = (key, quantityFor) => {
    queue(async () => {
      const line = lineByKey(key);
      if (!line) return;
      const quantity = quantityFor(line);
      if (quantity === null) return;
      qsa(`[data-key="${CSS.escape(key)}"]`).forEach((el) => el.classList.add('is-updating'));
      try {
        await cartChange(key, quantity);
      } catch (error) {
        qsa(`[data-key="${CSS.escape(key)}"]`).forEach((el) => el.classList.remove('is-updating'));
        toast(error.message);
      }
    });
  };

  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-qty-change]');
    const remove = event.target.closest('[data-remove]');
    const target = step || remove;
    if (!target) return;
    const line = target.closest('[data-key]');
    if (!line) return;
    event.preventDefault();
    const delta = remove ? null : Number(step.dataset.qtyChange);
    // Quantity is worked out when the queued request runs, from the line as it is then.
    run(line.dataset.key, (current) => {
      if (delta === null) return 0;
      const value = Number(qs('[data-qty-input]', current)?.value || 0);
      return Math.max(0, value + delta);
    });
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-qty-input]');
    if (!input) return;
    const line = input.closest('[data-key]');
    if (!line) return;
    const raw = input.value.trim();
    if (raw === '' || Number.isNaN(Number(raw))) {
      input.value = input.defaultValue; // an emptied box shouldn't delete the line
      return;
    }
    const value = Math.max(0, Math.floor(Number(raw)));
    run(line.dataset.key, () => value);
  });

  // Enter in a quantity box updates the line instead of submitting the cart form (which would check out).
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' && event.target.matches?.('[data-qty-input]')) {
      event.preventDefault();
      event.target.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  // Keep the bag in sync when the page is restored from the back/forward cache.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    fetchSectionHTML(`${config.routes.root}?section_id=${CART_SECTION}`)
      .then(renderCartDrawer)
      .catch(() => {});
  });
}

/* ---------- Search ---------- */
function initSearch() {
  const dialog = qs('#SearchOverlay');
  if (!dialog) return;
  const form = qs('[data-predictive-search]', dialog);
  const input = qs('input[type="search"]', dialog);
  const results = qs('[data-predictive-results]', dialog);
  const status = qs('[data-predictive-status]', dialog);
  const suggest = qs('[data-search-suggest]', dialog);
  let controller;
  let timer;
  let suggestLoaded = false;

  dialog.addEventListener('sheet:open', () => {
    setTimeout(() => input.focus(), 60);
    if (!suggestLoaded && suggest) {
      suggestLoaded = true;
      fetchSectionHTML(`${config.routes.root}?section_id=search-suggest`)
        .then((html) => {
          const section = qs('.shopify-section', parseHTML(html)) || parseHTML(html).body;
          suggest.innerHTML = section.innerHTML;
        })
        .catch(() => {});
    }
  });

  if (!config.settings.predictiveSearch) return;

  const run = async (term) => {
    controller?.abort();
    controller = new AbortController();
    const params = new URLSearchParams({
      q: term,
      'resources[type]': 'product,collection,article,page',
      'resources[limit]': '6',
      'resources[options][prefix]': 'last',
      section_id: 'predictive-search',
    });
    try {
      const response = await fetch(`${config.routes.predictiveSearch}?${params}`, { signal: controller.signal });
      if (!response.ok) throw new Error(String(response.status));
      const doc = parseHTML(await response.text());
      const section = qs('#shopify-section-predictive-search', doc) || doc.body;
      results.innerHTML = section.innerHTML;
      const count = qs('[data-predictive-count]', results)?.dataset.predictiveCount || '0';
      if (status) status.textContent = (config.strings.resultsCount || '__n__ results').replace('__n__', count);
    } catch (error) {
      if (error.name !== 'AbortError') results.innerHTML = '';
    }
  };

  input.addEventListener('input', () => {
    clearTimeout(timer);
    const term = input.value.trim();
    if (!term) {
      controller?.abort();
      results.innerHTML = '';
      if (status) status.textContent = '';
      return;
    }
    timer = setTimeout(() => run(term), 220);
  });

  form.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowDown' && event.target === input) {
      const first = qs('a', results) || qs('a', suggest);
      if (first) {
        event.preventDefault();
        first.focus();
      }
    }
  });
  dialog.addEventListener('keydown', (event) => {
    if (!['ArrowDown', 'ArrowUp'].includes(event.key) || event.target === input) return;
    const links = qsa('a', results.innerHTML ? results : suggest);
    const index = links.indexOf(document.activeElement);
    if (index === -1) return;
    event.preventDefault();
    const next = links[index + (event.key === 'ArrowDown' ? 1 : -1)];
    (next || input).focus();
  });
}

/* ---------- Collection sorting (Section Rendering API) ---------- */
function initCollection() {
  const section = qs('[data-collection]');
  if (!section) return;
  const select = qs('[data-sort]', section);
  const results = qs('[data-results]', section);
  if (!select || !results) return;
  let controller;
  select.addEventListener('change', async () => {
    controller?.abort();
    controller = new AbortController();
    const { signal } = controller;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', select.value);
    url.searchParams.delete('page');
    results.classList.add('is-loading');
    try {
      const fetchUrl = new URL(url);
      fetchUrl.searchParams.set('section_id', section.dataset.sectionId);
      const response = await fetch(fetchUrl, { signal });
      if (!response.ok) throw new Error(String(response.status));
      const doc = parseHTML(await response.text());
      const next = qs('[data-results]', doc);
      if (next) {
        results.replaceChildren(...next.childNodes);
        initReveals(results);
        syncSaved();
      }
      history.replaceState({}, '', url);
    } catch (error) {
      if (error.name !== 'AbortError') window.location.href = url.toString();
    } finally {
      if (!signal.aborted) results.classList.remove('is-loading');
    }
  });
}

/* ---------- Email forms: Shopify customer form + Klaviyo consent bridge ---------- */
function initNotifyForms() {
  qsa('[data-notify-form]').forEach((form) => {
    form.addEventListener('submit', async (event) => {
      const submit = qs('[data-submit]', form);
      const email = qs('input[type="email"]', form)?.value?.trim();
      if (submit) {
        submit.classList.add('is-loading');
        submit.setAttribute('aria-busy', 'true');
      }
      if (form.dataset.klaviyoSubmitted === '1') {
        event.preventDefault(); // already on its way
        return;
      }
      const companyId = form.dataset.klaviyoCompany;
      const listId = form.dataset.klaviyoList;
      if (!companyId || !listId || !email) return;
      event.preventDefault();
      form.dataset.klaviyoSubmitted = '1';
      try {
        await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`, {
          method: 'POST',
          keepalive: true,
          signal: AbortSignal.timeout ? AbortSignal.timeout(3000) : undefined,
          headers: { 'content-type': 'application/json', revision: '2023-12-15' },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: form.dataset.klaviyoSource || 'VØRIX notify form',
                profile: { data: { type: 'profile', attributes: { email } } },
              },
              relationships: { list: { data: { type: 'list', id: listId } } },
            },
          }),
        });
      } catch (_) {
        /* Shopify's own customer form still records the subscriber below. */
      } finally {
        HTMLFormElement.prototype.submit.call(form);
      }
    });
  });
  if (qs('[data-access-success], [data-notify-success]')) storage.raw('vorix.zeroHourJoined', '1');
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    qsa('[data-notify-form]').forEach((form) => {
      delete form.dataset.klaviyoSubmitted;
      const submit = qs('[data-submit]', form);
      submit?.classList.remove('is-loading');
      submit?.removeAttribute('aria-busy');
    });
  });
}

/* ---------- Zero Hour popup ---------- */
function initAccessPopup() {
  const popup = qs('[data-access-popup]');
  if (!popup) return;
  const closedKey = 'vorix.accessPopup.closedAt';
  const lastClosed = Number(storage.raw(closedKey) || 0);
  const week = 7 * 24 * 60 * 60 * 1000;
  const endAt = new Date(popup.dataset.discountEnd || '').getTime();
  const expired = Number.isFinite(endAt) && Date.now() >= endAt;
  // Shopify marks every customer form as posted; only the popup's own post (#vorix-popup-form) counts here.
  const success = Boolean(qs('[data-access-success]', popup)) && location.hash === '#vorix-popup-form';
  const joined = storage.raw('vorix.zeroHourJoined') === '1';
  const due = !expired && !joined && (!lastClosed || Date.now() - lastClosed > week);
  popup.addEventListener('sheet:close', () => storage.raw(closedKey, String(Date.now())));

  if (success) {
    openSheet(popup.id);
    return;
  }
  if (!due || /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(cart|checkout|account)(\/|$)/i.test(location.pathname)) return;
  // Wait for the configured delay and a sign of interest (a scroll), and never interrupt another open sheet.
  let engaged = false;
  window.addEventListener('scroll', () => (engaged = true), { once: true, passive: true });
  setTimeout(() => {
    const tryOpen = () => {
      if (qs('dialog[open]')) return setTimeout(tryOpen, 4000);
      if (!engaged) return window.addEventListener('scroll', tryOpen, { once: true, passive: true });
      openSheet(popup.id);
    };
    tryOpen();
  }, Number(popup.dataset.delay || 8000));
}

/* ---------- Small utilities ---------- */
function initUtilities() {
  qsa('[data-ticker-pause]').forEach((btn) =>
    btn.addEventListener('click', () => {
      const wrap = btn.closest('[data-ticker-wrap]');
      const paused = wrap.classList.toggle('is-paused');
      btn.setAttribute('aria-pressed', String(paused));
      btn.textContent = paused ? 'Play' : 'Pause';
    }),
  );

  document.addEventListener('click', async (event) => {
    const copy = event.target.closest('[data-copy]');
    if (copy) {
      try {
        await navigator.clipboard.writeText(copy.dataset.copy);
        toast(`Code ${copy.dataset.copy} copied`);
      } catch (_) {
        toast(`Your code is ${copy.dataset.copy}`);
      }
      return;
    }
    const share = event.target.closest('[data-share]');
    if (share) {
      const data = { title: share.dataset.shareTitle || document.title, url: share.dataset.shareUrl || location.href };
      try {
        if (navigator.share) await navigator.share(data);
        else {
          await navigator.clipboard.writeText(data.url);
          toast(config.strings.linkCopied);
        }
      } catch (_) {
        /* share sheet dismissed */
      }
    }
  });

  const focusTarget = qs('[data-focus-on-load]');
  if (focusTarget) focusTarget.focus();

  // Wrap tables in rich text so they scroll instead of breaking the layout.
  qsa('.rte table').forEach((table) => {
    if (table.parentElement.classList.contains('table-scroll')) return;
    const wrap = document.createElement('div');
    wrap.className = 'table-scroll';
    wrap.tabIndex = 0;
    wrap.setAttribute('role', 'region');
    wrap.setAttribute('aria-label', 'Table');
    wrap.style.overflowX = 'auto';
    table.before(wrap);
    wrap.append(table);
  });
}

/* ---------- Product image morph into the product page ---------- */
function initViewTransitions() {
  if (!('onpageswap' in window) || motion() === 'off') return;
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-card-link]');
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey) return;
    qsa('.plate[style*="product-hero"]').forEach((p) => p.style.removeProperty('--vt'));
    const plate = qs('.card__plate', link);
    if (plate) plate.style.setProperty('--vt', 'product-hero');
  });
  window.addEventListener('pageshow', () => {
    qsa('.card__plate').forEach((p) => p.style.removeProperty('--vt'));
  });
}

/* ---------- Boot ---------- */
config.api = { openSheet, closeSheet, toast, cartAdd, syncSaved, initReveals, motion, storage, finePointer };
window.vorix = config;

const yieldToMain = () =>
  globalThis.scheduler?.yield ? globalThis.scheduler.yield() : new Promise((resolve) => setTimeout(resolve, 0));

async function boot() {
  // Visible first: the hero moment, header and countdown; everything else after the main thread breathes.
  const steps = [
    initHero,
    initSheets,
    initHeader,
    initCountdowns,
    initReveals,
    initTabs,
    initViewToggle,
    initSaved,
    initCart,
    initRecent,
    initSearch,
    initCollection,
    initNotifyForms,
    initUtilities,
    initViewTransitions,
    initAccessPopup,
  ];
  for (const step of steps) {
    try {
      step();
    } catch (error) {
      console.error('[vorix]', step.name, error);
    }
    await yieldToMain();
  }
  config.ready = true;
  document.dispatchEvent(new CustomEvent('vorix:ready'));
}
boot();

document.addEventListener('shopify:section:load', (event) => {
  initReveals(event.target);
  initTabs(event.target);
  syncSaved();
});
